import { AlertDigestMode, AlertRecipient, AlertRecipients, AlertSeverity } from "@app/shared";
import { ModelHelpers } from "@app/shared/src/hooks";
import { FC } from "react";
import { toastError, toastSuccess } from "../../../helpers";
import { useConfirmDialog, useEditModelDialog, useFetch } from "../../../hooks";
import { IconLink, Icons, PageContainer, SectionIntro, SortableTable, StatusDot } from "../../shared";
import { DropDownMenu } from "../../shared/DropDownMenu";
import { CheckboxInput, EmailInput, EnumSelect, InputRow, Select } from "../../shared/inputs";
import { digestModeLabel, enumLabel, minSeverityLabel, minSeverityOptions, zonedTime } from "./alertDisplay";
import { AlertScope, AlertScopeInputs, useAlertScopeOptions } from "./AlertScope";

type AlertRecipientTableProps = {
    organizationId: string;
};

// Email addresses that aren't dashboard users (a maintenance alias, an on-call inbox).
// A new address gets a verification email and stays inactive until the link is clicked,
// so a typo can't silently swallow alerts. There is no update endpoint for recipients —
// change one by removing it and adding the corrected address (which re-verifies).
export const AlertRecipientTable: FC<AlertRecipientTableProps> = ({ organizationId }) => {
    const [recipients, , recipientHelpers] = useFetch(() => AlertRecipients.list(organizationId), [organizationId]);
    const { scopeLabel } = useAlertScopeOptions(organizationId);
    const recipientDialog = useEditModelDialog(AlertRecipient);
    const confirmDialog = useConfirmDialog();

    // Two separate ways an address can be silently inert, counted for the banner below.
    const unsubscribedCount = recipients?.filter((r) => r.unsubscribedAtUtc).length ?? 0;
    const pendingCount = recipients?.filter((r) => !r.unsubscribedAtUtc && !r.isVerified).length ?? 0;
    const notReceiving = recipients?.filter((r) => r.unsubscribedAtUtc || !r.isVerified) ?? [];

    // Not wrapped in try/catch on purpose — useEditModelDialog surfaces a failed save in
    // the dialog's error summary instead of silently doing nothing.
    const saveRecipient = async (toSave: Partial<AlertRecipient>) => {
        await AlertRecipients.create(toSave as AlertRecipient);
        recipientHelpers.refreshData();
        toastSuccess("Recipient Added", `A verification email has been sent to ${toSave.emailAddress}.`);
    };

    const handleAddOnClick = () => {
        recipientDialog.show(saveRecipient, "Add Email Recipient", {
            organizationId,
            minSeverity: AlertSeverity.Warning,
            digestMode: AlertDigestMode.Immediate,
            notifyOnAutoResolve: false,
            // "" not undefined: class-validator's MaxLength FAILS on undefined, and the TS
            // exporter emits @MaxLength for [MaxLength] on a nullable string without an
            // accompanying @IsOptional(). Left undefined, verificationToken fails validation
            // and Save shows a bare "correct the following errors" with nothing to correct —
            // no input is bound to it, so there is nowhere to put an inline message.
            verificationToken: "",
        });
    };

    const handleResendOnClick = async (recipient: AlertRecipient | undefined) => {
        if (!recipient) return;
        try {
            await AlertRecipients.resendVerification(recipient.id);
            recipientHelpers.refreshData();
            toastSuccess("Verification Sent", `A new verification email has been sent to ${recipient.emailAddress}.`);
        } catch (e) {
            toastError("Error Sending Verification", e + "");
        }
    };

    const handleDeleteOnClick = (toDelete: AlertRecipient | undefined) => {
        if (!toDelete) return;
        confirmDialog.show("Delete Recipient?", `${toDelete.emailAddress} will stop receiving alerts for this organization.`, "Delete", async () => {
            try {
                await AlertRecipients.deleteRecipient(toDelete.id);
                recipientHelpers.refreshData();
                toastSuccess("Recipient Deleted");
            } catch (e) {
                toastError("Error Deleting Recipient", e + "");
            }
        });
    };

    // Organization and location scope are mutually exclusive, so switching replaces the model.
    const applyScope = (model: Partial<AlertRecipient>, helpers: ModelHelpers<AlertRecipient>, scope: AlertScope) => {
        const next = new AlertRecipient();
        Object.assign(next, model, { organizationId: undefined, facilityId: undefined }, scope);
        helpers.replaceModel(next);
    };

    return (
        <>
            <PageContainer
                Icon={Icons.Mail}
                title="Email Recipients"
                titleRight={
                    <div className="flex justify-center items-center">
                        <IconLink Icon={Icons.PlusCircle} onClick={handleAddOnClick}>
                            Add New
                        </IconLink>
                    </div>
                }
            >
                <SectionIntro title="Alert emails for people and inboxes without a dashboard login.">
                    <p>
                        Use this for a shared maintenance inbox, an on-call alias, or a contractor who needs to know when a
                        device goes down but doesn't log in here. Anyone who <em>does</em> have a login should set themselves up
                        on the <strong>My Subscriptions</strong> tab instead, so they control their own settings.
                    </p>
                    <p>
                        Addresses added here apply to the whole organization (or one location), not just to you — every alert
                        matching the severity you choose is emailed to them, including when it's acknowledged and when a person
                        resolves it, so nobody has to log in to find out whether a problem is being dealt with. An alert that
                        clears on its own is left out unless you ask for it when you add or edit the address.
                    </p>
                    <p>
                        A new address receives a verification email first and gets <strong>nothing</strong> until someone clicks
                        the link in it. That's deliberate: it means a mistyped address can't quietly swallow alerts while
                        everyone assumes they're being delivered. Until then it shows as <strong>Pending</strong> — use Resend
                        Verification if the first email went astray.
                    </p>
                    <p>
                        Every alert email carries an unsubscribe link, so a recipient can stop them without going through you.
                        An address that has done so shows as <strong>Unsubscribed</strong> and receives nothing until it opts
                        back in — you can ask again, but only they can accept.
                    </p>
                </SectionIntro>
                {/* A red dot in a long table is easy to miss, and the consequence of missing it is that
                    an address everyone believes is covered receives nothing. Stated as a count above
                    the table so it is visible on arrival. */}
                {notReceiving.length > 0 && (
                    <div className="flex items-start gap-3 p-3 mb-3 border border-separators-page bg-background-page-highlight-gray text-sm">
                        <Icons.AlertTriangle className="shrink-0 mt-1 text-brand-primary" size={18} />
                        <div>
                            <div className="font-semibold">
                                {notReceiving.length} of {recipients?.length ?? 0} addresses {notReceiving.length === 1 ? "is" : "are"} not receiving alerts.
                            </div>
                            <div>
                                {unsubscribedCount > 0 && (
                                    <>
                                        <strong>{unsubscribedCount} unsubscribed</strong> (they asked to stop — only they can opt back in
                                        {pendingCount > 0 ? "), " : "). "}
                                    </>
                                )}
                                {pendingCount > 0 && (
                                    <>
                                        <strong>{pendingCount} awaiting verification</strong> (nobody has clicked the link yet).
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <SortableTable
                    initialSortKey="emailAddress"
                    isLoading={recipientHelpers.isLoading}
                    emptyValue={<p className="m-5">No Email Recipients Found</p>}
                    columns={[
                        {
                            header: "Status",
                            width: 50,
                            dataKey: "isVerified",
                            // Three states, not two: an address can also have unsubscribed itself from a
                            // link in an email, which is not the same as never having confirmed and must
                            // not look like it — the fix for one is to resend, the fix for the other is to
                            // ask the person whether they want it at all.
                            renderFunc: (r) => (
                                <div className="flex items-center h-full">
                                    <StatusDot
                                        status={r?.unsubscribedAtUtc ? "error" : r?.isVerified ? "ok" : "warn"}
                                        sizeClasses="w-3 h-3"
                                        hoverMessage={
                                            r?.unsubscribedAtUtc
                                                ? `Unsubscribed ${zonedTime(r?.unsubscribedAtUtc)} — this address asked to stop receiving alerts`
                                                : r?.isVerified
                                                  ? `Verified ${zonedTime(r?.verifiedAtUtc)}`
                                                  : `Awaiting verification — email sent ${zonedTime(r?.verificationSentAtUtc)}`
                                        }
                                    />
                                </div>
                            ),
                        },
                        {
                            header: "Email",
                            width: 250,
                            dataKey: "emailAddress",
                            filterable: true,
                            filterType: "text",
                        },
                        {
                            header: "Scope",
                            width: 150,
                            sortable: false,
                            renderFunc: (r) => (r ? scopeLabel(r) : ""),
                        },
                        {
                            header: "Notify About",
                            width: 150,
                            dataKey: "minSeverity",
                            renderFunc: (r) => minSeverityLabel(r?.minSeverity),
                        },
                        {
                            header: "Delivery",
                            width: 120,
                            dataKey: "digestMode",
                            renderFunc: (r) => digestModeLabel(r?.digestMode),
                        },
                        {
                            header: "State",
                            width: 120,
                            renderFunc: (r) => (r?.unsubscribedAtUtc ? "Unsubscribed" : r?.isVerified ? "Verified" : "Pending"),
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (r) => (
                                <DropDownMenu>
                                    {/* Also offered to an unsubscribed address: sending again asks for
                                        consent a second time, and the opt-out lifts only if they confirm. */}
                                    {(!r?.isVerified || r?.unsubscribedAtUtc) && (
                                        <IconLink Icon={Icons.Mail} onClick={() => handleResendOnClick(r)}>
                                            {r?.unsubscribedAtUtc ? "Ask Again to Subscribe" : "Resend Verification"}
                                        </IconLink>
                                    )}
                                    <IconLink Icon={Icons.Trash} onClick={() => handleDeleteOnClick(r)}>
                                        Delete
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={recipients}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {recipientDialog.renderDialog((model, helpers) => (
                <>
                    <InputRow>
                        <EmailInput label="Email Address" placeholder="alerts@example.com" {...helpers.bindingsFor("emailAddress")} />
                    </InputRow>
                    <AlertScopeInputs organizationId={organizationId} scope={model} onChange={(scope) => applyScope(model, helpers, scope)} />
                    <InputRow>
                        <Select
                            label="Minimum Severity"
                            options={minSeverityOptions}
                            value={model.minSeverity}
                            onChange={(e) => helpers.updateModel("minSeverity", Number(e.target.value) as AlertSeverity)}
                        />
                    </InputRow>
                    <InputRow>
                        <EnumSelect
                            label="Delivery"
                            enumType={AlertDigestMode}
                            nameToDisplayConverter={(key) => enumLabel(key)}
                            value={model.digestMode}
                            onChange={(value) => helpers.updateModel("digestMode", (value ?? AlertDigestMode.Immediate) as AlertDigestMode)}
                        />
                    </InputRow>
                    <InputRow>
                        <CheckboxInput
                            label="Email this address when an alert clears on its own"
                            checked={model.notifyOnAutoResolve ?? false}
                            onChange={(e) => helpers.updateModel("notifyOnAutoResolve", e.target.checked)}
                        />
                        <div className="text-gray-500 text-sm">
                            Off by default. A self-clearing alert means the device stopped reporting the problem, so there is
                            nothing to do about it — and on a device with a flaky connection it doubles the mail. A resolution
                            someone performed is always sent.
                        </div>
                    </InputRow>
                    <InputRow>
                        <div className="text-gray-500 text-sm">
                            The address receives a verification email and stays inactive until the link in it is clicked.
                        </div>
                    </InputRow>
                </>
            ))}
        </>
    );
};
